<template>
  <Bar v-show="!showChartExample" ref="ProfitAndLossChartBar" :data="chartData" :options="options" :plugins="[pluginSetupHighDpi,pluginDrawSalesStartMonth,pluginUpdateScaleTicksLabel]"/>
</template>

<script>
  import _ from 'lodash'
  import 'chart.js/auto';
  import { Bar } from 'vue-chartjs'
  import VueTypes from 'vue-types'
  import PL from './profit-and-loss'

  export default {
    name: 'ProfitAndLossChartV5',
    mixins: [PL],
    props: {
      format: VueTypes.string.def('text/html'),
      animationDuration: VueTypes.number.def(1000),
      showChartExample: VueTypes.bool.def(false)
    },
    components: { Bar },
    data () {
      return {
        salesDataset: [],
        operatingProfitsDataset: [],

        options: {
          maintainAspectRatio: false,
          responsive: false,
          responsiveAnimationDuration: this.animationDuration,
          animation: {
            duration: this.animationDuration
          },
          plugins: {
            legend: {
              labels: {
                fontColor: '#000000'
              }
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  return context.dataset.label + ' : ' + context.raw.toFixed(2) + ' 百万円'
                },
                title: function (context) {
                  let year = _.toInteger(context[0].label / 12)
                  let month = _.toInteger(context[0].label % 12)
                  if (month === 0) {
                    year -= 1
                    month = 12
                  }
                  return `${year}年 ${month}ヶ月目`
                }
              }
            }
          },
          interaction: {
            mode: 'index'
          },
          scales: {
            x: {
              axis: 'x',
              grid: {
                display: false
              },
              ticks: {
                fontColor: '#000000',
                maxRotation: 0,
                minRotation: 0,
                autoSkip: true,
                maxTicksLimit: 9
              },
              title: {
                fontColor: '#000000',
                display: true,
                text: '検討開始からの時間(ヶ月)＊点線は販売開始月'
              },
            },
            y: {
              axis: 'y',
              grid: {
                display: true
              },
              beginAtZero: true,
              type: 'linear',
              position: 'right',
              ticks: {
                fontColor: '#000000',
              },
              title: {
                fontColor: '#000000',
                display: true,
                text: '単月売上・単月営業利益(百万円)'
              },
            },
            y_mop: {
              axis: 'y',
              grid: {
                display: false
              },
              beginAtZero: true,
              type: 'linear',
              position: 'left',
              ticks: {
                fontColor: '#000000',
              },
              title: {
                fontColor: '#000000',
                display: true,
                text: '累積営業利益(百万円)'
              },
            }
          }
        }
      }
    },
    computed: {

      chartData () {
        // 単月売上
        this.salesDataset = this.salesPerMonths().map((sales) => sales / this.unit)

        // 単月営業利益
        this.operatingProfitsDataset = this.operatingProfitsPerMonthsForFunds().map((profit) => profit / this.unit)

        // 累積営業利益
        const accumulated = this.accumulatedOperatingProfits

        return {
          labels: _.times(this.beforeMonths + 36, (i) => `${i + 1}`),
          datasets: [
            {
              type: 'bar',
              label: '単月売上',
              backgroundColor: 'rgba(24, 42, 135, 1)',
              borderColor: 'rgba(24, 42, 135, 1)',
              data: this.salesDataset
            },
            {
              type: 'bar',
              label: '単月営業利益',
              backgroundColor: 'rgba(233, 85, 21,1)',
              borderColor: 'rgba(233, 85, 21, 1)',
              data: this.operatingProfitsDataset
            },
            {
              type: 'line',
              label: '累積営業利益',
              backgroundColor: 'rgba(0, 138, 25, 1)',
              borderColor: 'rgba(0, 138, 25, 1)',
              fill: false,
              radius: 1,
              yAxisID: 'y_mop',
              data: accumulated
            }
          ]
        }
      },

      accumulatedOperatingProfits () {
        return _.reduce(this.operatingProfitsDataset, (r, a, i) => {
          const subtotal = (i && r[i - 1]) || 0
          const total = subtotal + a
          r.push(total)
          return r
        }, [])
      },

      pluginSetupHighDpi () {
        return  {
          id: 'setupHighDpi',
          beforeInit: (chart) => {
            const canvas = chart.canvas
            if (canvas) {
              var dpr = window.devicePixelRatio || 1
              if (this.format === 'application/pdf') {
                dpr = 3
              }
              const rect = canvas.getBoundingClientRect()
              canvas.width = rect.width * dpr
              canvas.height = rect.height * dpr
              canvas.style.width = rect.width + 'px'
              canvas.style.height = rect.height + 'px'
              const ctx = canvas.getContext('2d')
              ctx.scale(dpr, dpr)
            }
          }
        }
      },
      // 販売開始月を明示
      pluginDrawSalesStartMonth () {
        return {
          id: 'drawSalesStartMonth',
          afterDatasetsDraw: (chart) => {
            if (chart.config.data.datasets.length === 0) {
              return
            }
            const meta = chart.getDatasetMeta(0)
            if (meta) {
              const data = meta.data
              const startData = data[this.beforeMonths]
              if (!startData) {
                return
              }
              const {x, y, base, width, height} = startData.getProps(['x', 'y', 'base', 'width', 'height'])
              let left, right, top, bottom, half;
              if (startData.horizontal) {
                half = height / 2;
                left = Math.min(x, base);
                right = Math.max(x, base);
                top = y - half;
                bottom = y + half;
              } else {
                half = width / 2;
                left = x - half;
                right = x + half;
                top = Math.min(y, base);
                bottom = Math.max(y, base);
              }
              const yTop = top
              const yBottom = bottom
              const ctx = chart.ctx
              ctx.beginPath()
              ctx.lineWidth = 1
              ctx.setLineDash([15, 5])
              ctx.strokeStyle = 'rgba(0, 0, 0, 0.7)'
              ctx.moveTo(x, yTop)
              ctx.lineTo(x, yBottom)
              ctx.stroke()
            }
          }
        }
      },
      // グラフのメモリを修正
      pluginUpdateScaleTicksLabel () {
        return {
          id: 'updateScaleTicksLabel',
          afterDataLimits: (chart, args, options) => {
            let y = null
            let max = 0

            const yCopData = _.map(_.concat(this.salesDataset, this.operatingProfitsDataset), (v) => Math.abs(v))
            max = this.suggestTicksMaxValue(yCopData)

            y = chart.scales.y
            y.max = max
            y.min = -max

            const yMopData = _.map(this.accumulatedOperatingProfits, (v) => Math.abs(v))
            max = this.suggestTicksMaxValue(yMopData)

            y = chart.scales.y_mop
            y.max = max
            y.min = -max
          }
        }  
      }  
    },
    methods: {
      suggestTicksMaxValue (data) {
        let maxValue = _.max(data)
        maxValue = Math.ceil(Math.abs(maxValue))
        return _.ceil(maxValue, -(String(maxValue).length - 1))
      }
    },
  }
</script>

